import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, FlatList, TouchableOpacity,
  StyleSheet, ActivityIndicator, Alert, RefreshControl,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { reservationsAPI } from '../services/api';

const STATUS_CONFIG = {
  pendente:       { label: 'Pendente',       cor: '#f59e0b', bg: '#fef3c7' },
  confirmada:     { label: 'Confirmada',     cor: '#10b981', bg: '#d1fae5' },
  em_andamento:   { label: 'Em andamento',   cor: '#3b82f6', bg: '#dbeafe' },
  finalizada:     { label: 'Finalizada',     cor: '#6b7280', bg: '#f3f4f6' },
  cancelada:      { label: 'Cancelada',      cor: '#ef4444', bg: '#fee2e2' },
};

function BookingCard({ item, onCancel }) {
  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.pendente;
  const dtInicio = new Date(item.data_inicio).toLocaleDateString('pt-BR');
  const dtFim    = new Date(item.data_fim).toLocaleDateString('pt-BR');

  return (
    <View style={styles.card}>
      {/* Status badge + match badge */}
      <View style={styles.cardTop}>
        <View style={[styles.badge, { backgroundColor: cfg.bg }]}>
          <Text style={[styles.badgeText, { color: cfg.cor }]}>{cfg.label}</Text>
        </View>
        {item.houve_match && (
          <View style={styles.matchBadge}>
            <Text style={styles.matchBadgeText}>🔗 Match aplicado</Text>
          </View>
        )}
      </View>

      {/* Veículo */}
      <Text style={styles.cardModelo}>{item.modelo}</Text>
      <Text style={styles.cardPlaca}>🔖 {item.placa}</Text>

      {/* Rota */}
      <View style={styles.routeRow}>
        <Text style={styles.routeCity}>{item.origem}</Text>
        <Text style={styles.routeArrow}>→</Text>
        <Text style={styles.routeCity}>{item.destino}</Text>
      </View>

      {/* Datas */}
      <Text style={styles.cardDatas}>📅 {dtInicio}  até  {dtFim}</Text>

      {/* Preço */}
      <View style={styles.precoRow}>
        <Text style={styles.precoLabel}>Total pago:</Text>
        <Text style={styles.precoValor}>R$ {parseFloat(item.preco_total || 0).toFixed(2)}</Text>
      </View>

      {/* Proprietário */}
      <Text style={styles.cardOwner}>👤 {item.proprietario_nome}</Text>

      {/* Ação cancelar */}
      {item.status === 'pendente' && (
        <TouchableOpacity style={styles.btnCancelar} onPress={() => onCancel(item.reserva_id)}>
          <Text style={styles.btnCancelarText}>Cancelar reserva</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

export default function MyBookingsScreen() {
  const [reservas,   setReservas]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const carregar = async () => {
    try {
      const { data } = await reservationsAPI.minhas();
      setReservas(data);
    } catch (err) {
      Alert.alert('Erro', err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Recarrega toda vez que a tela entra em foco
  useFocusEffect(useCallback(() => { carregar(); }, []));

  const handleCancel = (id) => {
    Alert.alert(
      'Cancelar Reserva',
      'Tem certeza que deseja cancelar esta reserva?',
      [
        { text: 'Não', style: 'cancel' },
        {
          text: 'Sim, cancelar',
          style: 'destructive',
          onPress: async () => {
            try {
              await reservationsAPI.cancelar(id);
              carregar();
            } catch (err) {
              Alert.alert('Erro', err.message);
            }
          },
        },
      ]
    );
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#F5C518" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Minhas Reservas</Text>
        <Text style={styles.headerCount}>{reservas.length} reserva{reservas.length !== 1 ? 's' : ''}</Text>
      </View>

      {reservas.length === 0 ? (
        <View style={styles.empty}>
          <Text style={styles.emptyIcon}>📋</Text>
          <Text style={styles.emptyTitle}>Sem reservas ainda</Text>
          <Text style={styles.emptyText}>Faça sua primeira reserva e aproveite o matching P2P!</Text>
        </View>
      ) : (
        <FlatList
          data={reservas}
          keyExtractor={item => String(item.reserva_id)}
          renderItem={({ item }) => <BookingCard item={item} onCancel={handleCancel} />}
          contentContainerStyle={{ padding: 16, paddingBottom: 32 }}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => { setRefreshing(true); carregar(); }}
              colors={['#F5C518']}
            />
          }
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container:       { flex: 1, backgroundColor: '#f9f9f9' },
  center:          { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header:          { backgroundColor: '#F5C518', paddingTop: 56, paddingBottom: 20, paddingHorizontal: 24, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' },
  headerTitle:     { fontSize: 24, fontWeight: '900', color: '#1a1a1a' },
  headerCount:     { fontSize: 14, color: '#444' },
  card:            { backgroundColor: '#fff', borderRadius: 16, padding: 18, marginBottom: 14, shadowColor: '#000', shadowOpacity: 0.07, shadowRadius: 8, elevation: 3 },
  cardTop:         { flexDirection: 'row', gap: 8, marginBottom: 12 },
  badge:           { borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4 },
  badgeText:       { fontSize: 12, fontWeight: '700' },
  matchBadge:      { backgroundColor: '#fff3c4', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4 },
  matchBadgeText:  { fontSize: 12, fontWeight: '600', color: '#7a5a00' },
  cardModelo:      { fontSize: 20, fontWeight: 'bold', color: '#1a1a1a' },
  cardPlaca:       { fontSize: 13, color: '#888', marginTop: 2, marginBottom: 12 },
  routeRow:        { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 },
  routeCity:       { fontSize: 15, fontWeight: '600', color: '#333' },
  routeArrow:      { fontSize: 18, color: '#F5C518' },
  cardDatas:       { fontSize: 13, color: '#888', marginBottom: 12 },
  precoRow:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9f9f9', borderRadius: 10, padding: 12, marginBottom: 8 },
  precoLabel:      { fontSize: 13, color: '#666' },
  precoValor:      { fontSize: 18, fontWeight: 'bold', color: '#1a1a1a' },
  cardOwner:       { fontSize: 13, color: '#aaa', borderTopWidth: 1, borderTopColor: '#f0f0f0', paddingTop: 10, marginTop: 4 },
  btnCancelar:     { marginTop: 12, paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: '#ef4444', alignItems: 'center' },
  btnCancelarText: { color: '#ef4444', fontWeight: '600', fontSize: 14 },
  empty:           { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 },
  emptyIcon:       { fontSize: 64, marginBottom: 16 },
  emptyTitle:      { fontSize: 20, fontWeight: 'bold', color: '#1a1a1a', marginBottom: 8 },
  emptyText:       { fontSize: 14, color: '#888', textAlign: 'center' },
});
